/**
 * Answer-order shuffle for multiple-choice knowledge checks (display order only: option ids never change, so grading
 * and the Cyberfrog quiz POST are unaffected). One attempt = one seed; the order is stable while the learner answers
 * and in the review, and a new attempt (a reload or a replay) draws a new seed.
 *
 * Within one seed the slot of the correct answer never repeats on consecutive questions, so the correct answer can
 * not sit in the same slot for a whole quiz. Pure functions, no imports.
 */

export interface ShuffleOption {
  id: string;
  correct?: boolean;
}

function hash32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A fresh random seed for one attempt. */
export function newShuffleSeed(): string {
  try {
    const a = new Uint32Array(2);
    crypto.getRandomValues(a);
    return `${a[0].toString(36)}${a[1].toString(36)}`;
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

/** Order several option lists (one per question) for one attempt. */
export function shuffleOptionSets<T extends ShuffleOption>(sets: T[][], seed: string): T[][] {
  const rnd = mulberry32(hash32(seed));
  let prevSlot = -1;
  return sets.map((opts) => {
    const n = opts.length;
    if (n < 2) return opts.slice();
    const ci = opts.findIndex((o) => o.correct);
    const rest = opts.filter((_, i) => i !== ci);
    for (let i = rest.length - 1; i > 0; i--) {
      const j = Math.floor(rnd() * (i + 1));
      [rest[i], rest[j]] = [rest[j], rest[i]];
    }
    if (ci < 0) {
      prevSlot = -1;
      return rest;
    }
    const slots: number[] = [];
    for (let s = 0; s < n; s++) if (s !== prevSlot) slots.push(s);
    const slot = slots[Math.floor(rnd() * slots.length)];
    const out = rest.slice();
    out.splice(slot, 0, opts[ci]);
    prevSlot = slot;
    return out;
  });
}

/** Order one option list. */
export function shuffleOptions<T extends ShuffleOption>(opts: T[], seed: string): T[] {
  return shuffleOptionSets([opts], seed)[0];
}
